/**
 * Keyboard triage (WP-007): a pure mapping from a key press to a command, so the bindings are
 * tested without a DOM, and the cursor arithmetic the list uses.
 */
export type Command =
  | "next"
  | "prev"
  | "open"
  | "back"
  | "archive"
  | "trash"
  | "star"
  | "unread"
  | "move"
  | "tag"
  | "spam"
  | "reply"
  | "forward"
  | "compose"
  | "search"
  | "task"
  | "snooze"
  | "select"
  | "help";

export type View = "list" | "thread";

export interface KeyInput {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  /** Focus is in a text field: plain keys are typing, not commands. */
  typing: boolean;
}

const BINDINGS: Record<string, Command> = {
  j: "next",
  k: "prev",
  e: "archive",
  "#": "trash",
  // The delete key: Backspace on a Mac keyboard, Delete elsewhere.
  Delete: "trash",
  Backspace: "trash",
  s: "star",
  u: "unread",
  v: "move",
  l: "tag",
  "!": "spam",
  r: "reply",
  f: "forward",
  c: "compose",
  "/": "search",
  T: "task",
  z: "snooze",
  x: "select",
  "?": "help",
};

/** The help overlay's rows, in the order people learn them. */
export const HELP: ReadonlyArray<{ keys: string; does: string }> = [
  { keys: "j / k", does: "Next / previous conversation" },
  { keys: "Enter or o", does: "Open the conversation" },
  { keys: "Esc", does: "Back to the list" },
  { keys: "e", does: "Archive" },
  { keys: "# or Delete", does: "Trash, and open the next conversation" },
  { keys: "s", does: "Star or unstar" },
  { keys: "u", does: "Mark unread" },
  { keys: "v", does: "Move to a folder" },
  { keys: "l", does: "Add a tag" },
  { keys: "z", does: "Snooze until later" },
  { keys: "x", does: "Select the conversation (shift-click a box for a range)" },
  { keys: "!", does: "Report spam" },
  { keys: "r / f", does: "Reply / forward" },
  { keys: "c", does: "Compose" },
  { keys: "/", does: "Search" },
  { keys: "T", does: "Create a task from the email" },
  { keys: "?", does: "Show this help" },
];

export function commandFor(input: KeyInput, view: View): Command | null {
  if (input.metaKey || input.ctrlKey || input.altKey) return null;
  if (input.typing) return null;
  if (input.key === "Escape") return view === "thread" ? "back" : null;
  if (input.key === "Enter" || input.key === "o") return view === "list" ? "open" : null;
  const bound = BINDINGS[input.key];
  if (bound === "select" && view === "thread") return null;
  return bound === undefined ? null : bound;
}

/** The cursor after moving by `delta`, kept inside the list (an empty list has no cursor). */
export function moveCursor(index: number, delta: number, length: number): number {
  if (length === 0) return 0;
  return Math.max(0, Math.min(length - 1, index + delta));
}

/**
 * Where the cursor goes when the thread under it leaves the list (archive, trash...): it stays
 * on the same row, which is now the next thread, or the new last row.
 */
export function cursorAfterRemoval(index: number, lengthAfter: number): number {
  if (lengthAfter === 0) return 0;
  return Math.min(index, lengthAfter - 1);
}

/**
 * What opens after the open conversation leaves the list (trash, archive...): the row below it,
 * which takes its place, or the one above when it was the last; null when nothing is left or it
 * was not in the list (opened from search results). `index` is where the next one will sit.
 */
export function nextAfterRemoval<T extends { id: string }>(list: readonly T[], id: string): { entry: T; index: number } | null {
  const at = list.findIndex((e) => e.id === id);
  if (at === -1) return null;
  const below = list[at + 1];
  if (below !== undefined) return { entry: below, index: at };
  const above = list[at - 1];
  return above === undefined ? null : { entry: above, index: at - 1 };
}

/** Is focus in something that takes text, where plain keys must not trigger commands? */
export function isTyping(target: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return !["checkbox", "radio", "button", "submit"].includes(target.type);
  return target.isContentEditable || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}
